import React, { useEffect, useRef } from 'react';
import { AlertTriangle, X } from 'lucide-react';

const ConfirmModal = ({ open, title, message, confirmLabel = 'Delete', onConfirm, onCancel }) => {
  const cancelRef = useRef(null);

  useEffect(() => {
    if (open && cancelRef.current) cancelRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onCancel]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title" aria-describedby="confirm-message">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in" onClick={onCancel} />

      {/* Modal */}
      <div className="relative bg-card border border-border rounded-xl shadow-2xl w-full max-w-md animate-scale-in-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-start gap-4 p-6 pb-0">
          <div className="w-10 h-10 rounded-full bg-red-50 border border-red-200 flex items-center justify-center shrink-0">
            <AlertTriangle size={18} className="text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 id="confirm-title" className="font-heading font-semibold text-foreground text-base">{title}</h2>
            <p id="confirm-message" className="text-sm text-muted-foreground mt-1 leading-relaxed">{message}</p>
          </div>
          <button onClick={onCancel} className="p-1 text-muted-foreground hover:text-foreground rounded-md transition-colors cursor-pointer" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 p-6 pt-5">
          <button ref={cancelRef} onClick={onCancel}
            className="btn-outline">
            Cancel
          </button>
          <button onClick={onConfirm}
            className="btn-destructive">
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
